// Nonprofit setup — one plain choice per account drives the 501(c)(3) reports (olfin/nonprofit.py):
// expense accounts → Program / Management & general / Fundraising; income accounts → 990 revenue type.
// Each starts with a suggestion from the account name; the Treasurer confirms or changes it.
import React, { useEffect, useState } from 'react';
import axios from 'axios';
import { Button } from '../../components/ui/button';
import { HeartHandshake } from 'lucide-react';

const CARD = 'bg-white text-slate-800 border-slate-200';
const errOf = (e) => e?.response?.data?.detail || 'Request failed';

export function NonprofitSetupPage({ base, go }) {
  const [d, setD] = useState(null); const [val, setVal] = useState({});
  const [msg, setMsg] = useState(''); const [err, setErr] = useState('');
  const load = () => axios.get(`${base}/nonprofit/mapping`).then((r) => {
    setD(r.data); setVal(Object.fromEntries(r.data.accounts.map((a) => [a.account_id, a.value || a.suggested])));
  }).catch((e) => setErr(errOf(e)));
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [base]);
  const save = async () => {
    setErr(''); setMsg('');
    try {
      const r = await axios.put(`${base}/nonprofit/mapping`, { items: Object.entries(val).map(([account_id, value]) => ({ account_id, value })) });
      setMsg(`Saved ${r.data.saved} accounts`); load();
    } catch (e) { setErr(errOf(e)); }
  };
  if (!d) return <div className="text-slate-500">{err || 'Loading…'}</div>;
  const section = (kind, title, opts) => (
    <div className={`rounded-lg border ${CARD}`}>
      <div className="px-3 py-2 font-semibold border-b border-slate-200">{title}</div>
      <div className="max-h-[38vh] overflow-auto">
        <table className="w-full text-sm"><tbody>
          {d.accounts.filter((a) => a.kind === kind).map((a) => (
            <tr key={a.account_id} className="border-b border-slate-100" data-testid={`np-row-${a.account_id}`}>
              <td className="p-1.5">{a.name}{!a.value && <span className="ml-2 text-xs text-amber-600">suggested</span>}</td>
              <td className="p-1.5 w-72"><select className="h-8 w-full rounded border border-slate-300 bg-white px-2" value={val[a.account_id] || ''}
                onChange={(e) => setVal({ ...val, [a.account_id]: e.target.value })}>
                {Object.entries(opts).map(([k, l]) => <option key={k} value={k}>{l}</option>)}</select></td>
            </tr>))}
        </tbody></table>
      </div>
    </div>
  );
  return (
    <div className="space-y-3 text-slate-800 max-w-5xl" data-testid="np-setup-page">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold flex items-center gap-2"><HeartHandshake className="h-5 w-5 text-emerald-600" />Nonprofit setup</h2>
        <div className="flex gap-2">
          <Button variant="outline" onClick={() => go('reports')}>Reports</Button>
          <Button className="bg-emerald-600 text-white" onClick={save} data-testid="np-save">Save</Button>
        </div>
      </div>
      <p className="text-sm text-slate-500">These choices feed the Statement of Functional Expenses, Contributions by Donor and the Form 990 Summary.
        {' '}{d.confirmed} of {d.total} accounts confirmed — the rest use the suggestion shown until you save.</p>
      {msg && <p className="text-sm text-emerald-700">{msg}</p>}{err && <p className="text-sm text-rose-600">{err}</p>}
      {section('expense', 'Expenses — what is each one for?', d.functions)}
      {section('income', 'Income — which kind of revenue is it?', d.revenue_types)}
    </div>
  );
}
